import React, { PureComponent } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import client from "@culturehq/client";
import { classnames, Icon, ModalDialog, PlainButton, Tooltip } from "@culturehq/components";

import { errorService, t } from "app/utils";

import Flag from "components/common/Flag";

import LightboxPhoto from "./LightboxPhoto";
import DeletePhoto from "./DeletePhoto";
import PhotoTagging from "./tags/PhotoTagging";
import ChqModalDialog from "../ChqModalDialog";

const LightboxArrows = ({ onSwipeLeft, onSwipeRight }) => (
  <>
    <button aria-label={t("Go to previous")} type="button" className="gallery-lightbox__chevron-left" onClick={onSwipeLeft}>
      <Icon icon="chevron-left" />
    </button>
    <button aria-label={t("Go to next")} type="button" className="gallery-lightbox__chevron-right" onClick={onSwipeRight}>
      <Icon icon="chevron-right" />
    </button>
  </>
);

const LightboxActions = ({ activePhoto, tagging, onTaggingToggle, onDelete, onClose }) => (
  <div className="gallery-lightbox--actions">
    <a aria-label={t("Download")} href={activePhoto.image.originalUrl} download>
      <Icon icon="ios-download" />
    </a>
    {activePhoto.event && (
      <>
        <DeletePhoto photo={activePhoto} onDelete={onDelete} />
        <Flag action="flagPhoto" options={{ photoId: activePhoto && activePhoto.id }}>
          {onTrigger => (
            <PlainButton aria-label={t("Flag")} onClick={onTrigger}>
              <Icon icon="ios-flag" />
            </PlainButton>
          )}
        </Flag>
        <Tooltip tip={tagging ? t("Done tagging") : t("Tag colleagues in this photo")}>
          <PlainButton
            aria-label={t("Toggle tagging")}
            onClick={onTaggingToggle}
            className={classnames({ "gallery-lightbox--active": tagging })}
          >
            <Icon icon="tags" />
          </PlainButton>
        </Tooltip>
      </>
    )}
    <PlainButton aria-label={t("Close")} onClick={onClose}>
      <Icon icon="close" />
    </PlainButton>
  </div>
);

class LightboxModalWrapper extends PureComponent {
  containerRef = React.createRef();

  timeout = 0;

  static defaultProps = {
    onPhotoChange: () => {}
  };

  state = {
    fullSize: false,
    tagging: false,
    taggingPosition: null
  };

  componentDidUpdate(prevProps) {
    const { activePhoto, modalIsOpen } = this.props;

    if (modalIsOpen !== prevProps.modalIsOpen) {
      clearTimeout(this.timeout);

      if (modalIsOpen) {
        this.listener = window.addEventListener("keydown", this.handleKeyDown);
        this.timeout = setTimeout(() => this.setState({ fullSize: true }), 500);
      } else {
        this.setState({ fullSize: false });
      }
    }

    if ((activePhoto && activePhoto.id) !== (prevProps.activePhoto && prevProps.activePhoto.id)) {
      this.setState({ tagging: false, taggingPosition: null });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  handleSwipeLeft = () => {
    const { activePhoto, onPhotoChange, photos } = this.props;

    onPhotoChange(photos.findIndex(photo => photo.id === activePhoto.id) - 1);
  };

  handleSwipeRight = () => {
    const { activePhoto, onPhotoChange, photos } = this.props;

    onPhotoChange(photos.findIndex(photo => photo.id === activePhoto.id) + 1);
  };

  handleClose = () => {
    const { history, modalIsOpen, onClose } = this.props;

    window.removeEventListener("keydown", this.handleKeyDown);
    this.listener = null;

    this.setState({ tagging: false, taggingPosition: null });
    if (modalIsOpen) {
      const { location: { pathname } } = history;
      const paths = pathname.split("/");
      switch (paths[1]) {
        case "photos":
          history.push("/");
          break;
        default:
          break;
      }
    }
    onClose();
  };

  handleKeyDown = event => {
    switch (event.key) {
      case "ArrowLeft":
        this.handleSwipeLeft();
        break;
      case "ArrowRight":
        this.handleSwipeRight();
        break;
      default:
        break;
    }
  };

  handleTaggingToggle = () => {
    this.setState(({ tagging }) => ({ tagging: !tagging, taggingPosition: null }));
  };

  handleTagStart = taggingPosition => {
    this.setState({ taggingPosition });
  };

  handleTagEnd = async user => {
    const { activePhoto, onPhotoUpdate } = this.props;
    const { taggingPosition } = this.state;

    let options = { photoId: activePhoto.id, userId: user.id };

    if (taggingPosition.uuid) {
      options.uuid = taggingPosition.uuid;
    } else {
      options = { ...options, left: taggingPosition.left, top: taggingPosition.top };
    }

    try {
      const response = await client.createPhotoTag(options);

      if (response) {
        onPhotoUpdate({
          ...activePhoto,
          faces: (activePhoto.faces || []).filter(face => face.uuid !== taggingPosition.uuid),
          photoTags: [...activePhoto.photoTags, response.photoTag]
        });
      }
    } catch (error) {
      errorService.error(error);
    }

    this.setState({ taggingPosition: null });
  };

  handleTaggingClose = () => {
    this.setState({ taggingPosition: null });
  };

  handleTagDelete = async ({ id: photoTagId }) => {
    const { activePhoto, onPhotoUpdate } = this.props;

    try {
      await client.deletePhotoTag({ photoId: activePhoto.id, photoTagId });
    } catch (error) {
      errorService.error(error);
    }

    onPhotoUpdate({
      ...activePhoto,
      photoTags: activePhoto.photoTags.filter(({ id }) => id !== photoTagId)
    });
  };

  render() {
    const { modalIsOpen, photos, onPhotoCheerToggle, onDelete,
      activePhoto, actionablePhoto } = this.props;
    const { fullSize, tagging, taggingPosition } = this.state;

    if (!modalIsOpen) {
      return null;
    }

    return (
      <>
        <ChqModalDialog
          className={classnames("gallery-lightbox", { "gallery-lightbox-tagging": tagging })}
          entrance="zoomIn"
          onClose={this.handleClose}
        >
          <ModalDialog.Body>
            <div className="Lightbox-Image" ref={this.containerRef}>
              {activePhoto && (
                <div className="Story-Image">
                  <LightboxPhoto
                    activePhoto={activePhoto}
                    containerRef={this.containerRef}
                    fullSize={fullSize}
                    tagging={tagging}
                    actionablePhoto={actionablePhoto}
                    onTagDelete={this.handleTagDelete}
                    onTagStart={this.handleTagStart}
                    onPhotoCheerToggle={onPhotoCheerToggle}
                  />
                </div>
              )}

              {activePhoto && (
                <LightboxActions
                  activePhoto={activePhoto}
                  tagging={tagging}
                  onTaggingToggle={this.handleTaggingToggle}
                  onDelete={onDelete}
                  onClose={this.handleClose}
                />
              )}

              {photos.length > 1 && (
                <LightboxArrows
                  onSwipeLeft={this.handleSwipeLeft}
                  onSwipeRight={this.handleSwipeRight}
                />
              )}
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
        {taggingPosition && ReactDOM.createPortal(
          <PhotoTagging
            onClose={this.handleTaggingClose}
            onTag={this.handleTagEnd}
            position={taggingPosition}
          />,
          document.body
        )}
      </>
    );
  }
}

const LightboxModal = props => {
  const history = useHistory();

  return (
    <LightboxModalWrapper {...props} history={history} />
  );
};

export default LightboxModal;
